/**
 * Whether the row of frequent smileys sits above the input (see `composer-quick`
 * in Composer.tsx). Off unless it is asked for: it is the one part of the
 * composer that takes room without being typed into, and the smiley button next
 * to the field opens the full picker anyway — nothing is out of reach without it.
 *
 * Per person, like the theme and the smiley ranking, and stored the same
 * way: localStorage, because it is a preference rather than content. Moving it
 * onto the account later means changing `load` and `persist` and nothing else.
 */
import { useSyncExternalStore } from 'react';

const KEY = 'smileyBar.v1';

/** Hidden until someone turns it on — see the note above. */
const DEFAULT = false;

/** Exported for the test: anything but the stored «on» reads as off. */
export function parseSmileyBar(raw: string | null): boolean {
  if (raw === null) return DEFAULT;
  return raw === '1';
}

function load(): boolean {
  try {
    return parseSmileyBar(localStorage.getItem(KEY));
  } catch {
    // Private mode, or a store the browser has disabled.
    return DEFAULT;
  }
}

function persist(shown: boolean): void {
  try {
    localStorage.setItem(KEY, shown ? '1' : '0');
  } catch {
    // The choice still applies for this session, it just will not survive a
    // reload. A toggle is not worth a message about it.
  }
}

let current = load();
const listeners = new Set<() => void>();

/** Stable between changes, which is what useSyncExternalStore requires. */
export function smileyBarShown(): boolean {
  return current;
}

export function setSmileyBarShown(shown: boolean): void {
  current = shown;
  persist(current);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The setting, re-rendering every open composer the moment it changes. */
export function useSmileyBar(): boolean {
  return useSyncExternalStore(subscribe, smileyBarShown, smileyBarShown);
}
